/* import __COLOCATED_TEMPLATE__ from './pane.hbs.js'; */
var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Component from '@glimmer/component';
import { scheduleOnce } from '@ember/runloop';
import transitionEnd from 'ember-bootstrap/utils/transition-end';
import usesTransition from 'ember-bootstrap/utils/decorators/uses-transition';
import { guidFor } from '@ember/object/internals';
import { ref } from 'ember-ref-bucket';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';

/**
 The tab pane of a tab component.
 See [Components.Tab](Components.Tab.html) for examples.

 @class TabPane
 @namespace Components
 @extends Component
 @public
 */
let TabPane = (_dec = ref('mainNode'), _dec2 = usesTransition('fade'), _class = class TabPane extends Component {
  /**
   * @property id
   * @type string
   * @public
   */
  get id() {
    return this.args.id ?? guidFor(this);
  }

  /**
   * @property activeId
   * @private
   */
  get activeId() {
    return this.args.activeId ?? null;
  }

  /**
   * True if this pane is active (visible)
   *
   * @property isActive
   * @type boolean
   * @readonly
   * @private
   */
  get isActive() {
    return this.activeId === this.id;
  }

  /**
   * Used to apply Bootstrap's "active" class
   *
   * @property active
   * @type boolean
   * @default false
   * @private
   */

  /**
   * The title for this tab pane. This is used by the `bs-tab` component to automatically generate
   * the tab navigation.
   * See the [Components.Tab](Components.Tab.html) for examples.
   *
   * @property title
   * @type string
   * @default null
   * @public
   */
  get title() {
    return this.args.title ?? null;
  }

  /**
   * An optional group title used by the `bs-tab` component to group all panes with the same group title
   * under a common drop down in the tab navigation.
   * See the [Components.Tab](Components.Tab.html) for examples.
   *
   * @property groupTitle
   * @type string
   * @default null
   * @public
   */
  get groupTitle() {
    return this.args.groupTitle ?? null;
  }

  /**
   * Use fade animation when switching tabs.
   *
   * @property fade
   * @type boolean
   * @private
   */
  get fade() {
    return this.args.fade ?? true;
  }

  /**
   * The duration of the fade out animation
   *
   * @property fadeDuration
   * @type number
   * @default 150
   * @private
   */
  get fadeDuration() {
    return this.args.fadeDuration ?? 150;
  }

  /**
   * Use CSS transitions?
   *
   * @property usesTransition
   * @type boolean
   * @readonly
   * @private
   */

  /**
   * Show the pane
   *
   * @method show
   * @protected
   */
  show() {
    if (this.usesTransition) {
      if (!this._element) {
        // _element is initially set by `{{create-ref}}` which happens in next run loop, so can be undefined here.
        this.active = true;
        this.showContent = true;
      } else {
        transitionEnd(this._element, this.fadeDuration).then(() => {
          if (!this.isDestroyed) {
            this.active = true;
            this.showContent = true;
          }
        });
      }
    } else {
      this.active = true;
    }
  }

  /**
   * Hide the pane
   *
   * @method hide
   * @protected
   */
  hide() {
    if (this.usesTransition) {
      transitionEnd(this._element, this.fadeDuration).then(() => {
        if (!this.isDestroyed) {
          this.active = false;
        }
      });
      this.showContent = false;
    } else {
      this.active = false;
    }
  }
  showHide() {
    if (this.isActive) {
      this.show();
    } else {
      this.hide();
    }
  }
  _setActive() {
    this.active = this.isActive;
    this.showContent = this.isActive && this.fade;
  }
  constructor(owner, args) {
    super(owner, args);
    /**
     * @property id
     * @type null | HTMLElement
     */
    _initializerDefineProperty(this, "_element", _descriptor, this);
    _initializerDefineProperty(this, "active", _descriptor2, this);
    /**
     * Used to trigger the Bootstrap visibility classes.
     *
     * @property showContent
     * @type boolean
     * @default false
     * @private
     */
    _initializerDefineProperty(this, "showContent", _descriptor3, this);
    _initializerDefineProperty(this, "usesTransition", _descriptor4, this);
    args.registerChild?.(this);

    // isActive comes from parent component, so only available after render...
    scheduleOnce('afterRender', this, this._setActive);
  }
  willDestroy() {
    super.willDestroy();
    this.args.unregisterChild?.(this);
  }
  updateActive() {
    this.active = this.args.active;
  }
  updateShowContent() {
    this.showContent = this.args.showContent;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_element", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "active", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.active ?? false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "showContent", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.showContent ?? false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "usesTransition", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "showHide", [action], Object.getOwnPropertyDescriptor(_class.prototype, "showHide"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateActive", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateActive"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateShowContent", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateShowContent"), _class.prototype), _class);
export { TabPane as default };